import React, { useEffect, useRef, useState } from "react";
import "./InternetPlanCard.css";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import defaultProfilePic from '../images/user.png';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { FiCreditCard, FiRefreshCw, FiShield, FiUserCheck, FiKey, FiXCircle, FiCpu, FiLock, FiClock, FiFileText, FiMoreVertical, FiInfo, FiActivity } from "react-icons/fi";

const LINK_TYPE_LABEL = { internet: "Internet", dvpl: "DVPL", railtel: "RailTel" };

const InternetPlanCard = ({
  user,
  internetInfo,
  loading,
  // True while the initial account lookup is still in flight (i.e.
  // Customer/index.js's internetLoading state, from loadInternetCustomer()).
  // Without this, customerExists starts false before internetInfo has
  // ever been set, so this card showed "no account, link one" for a
  // moment on every load — even for customers who definitely have one —
  // before flipping to the real data. That false-negative flash is what
  // was reported as feeling broken.
  accountLoading,
  // Accounts are linked but their details failed to load — show a retry
  // instead of the misleading "No connection linked" message.
  accountLoadFailed,
  onRetry,
  onRecharge,
  onLinkAccount,
  onRemoveMac,
  onOnuDetails,
  onAuthLogs,
  onSessionHistory,
  onPassword,
  onChangeVerification,
  onVerifyAadhaar,
  // (key, close) => content of a detail tab (Password, Remove MAC, ONU details, ...).
  // When given, those actions open as tabs inside this card instead of pop-ups.
  renderPanel,
  // Set when this account's current period came from a combo recharge: the separate
  // Recharge / Advance Renewal buttons are replaced by a note (renew through Combo Recharge).
  comboLock,
  // false when the customer's operator lets Internet / DVPL customers be recharged without
  // Aadhaar verification (operator setting "Aadhaar verification required").
  aadhaarRequired = true,
  // Purely cosmetic — this card is fully provider-agnostic otherwise
  // (it just renders whatever internetInfo it's handed). Pass "DVPL"
  // when reusing it for the DVPL tab so the two empty-state strings
  // below say the right thing.
  providerLabel = "Internet",
  // Every broadband type this customer can be linked to (["internet", "dvpl", "railtel"]).
  // With more than one, the empty state's button asks which one, like "+ Link account".
  linkTypes,
  onLinkType,
}) => {
  const customerExists =
    internetInfo?.status === 200 &&
    internetInfo?.results &&
    typeof internetInfo.results === "object";

  const data = customerExists ? internetInfo.results : {};

  const parseExpiry = (expiry) => {
    if (!expiry) return null;

    // SQL format
    const sqlDate = new Date(expiry.replace(" ", "T"));
    if (!isNaN(sqlDate.getTime())) {
      return sqlDate;
    }

    // 25 Aug, 2026 12:09 am
    const match = expiry.match(
      /^(\d{1,2})\s([A-Za-z]{3}),\s(\d{4})\s+(\d{1,2}):(\d{2})\s(am|pm)$/i
    );

    if (!match) return null;

    const [, day, mon, year, hourStr, min, ampm] = match;

    const months = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    let hour = parseInt(hourStr);

    if (ampm.toLowerCase() === "pm" && hour !== 12) hour += 12;
    if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;

    return new Date(
      parseInt(year),
      months[mon],
      parseInt(day),
      hour,
      parseInt(min)
    );
  };

  const [profilePicURL, setProfilePicURL] = useState(null);
  const [idProofURL, setIdProofURL] = useState(null);
  const [addressProofURL, setAddressProofURL] = useState(null);

  // Phones/tablets: the actions sidebar collapses into a "⋮" menu.
  // Close it on an outside tap or Escape.
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef(null);

  // Selected detail tab. "overview" = the account details below the header.
  const [tab, setTab] = useState("overview");
  const tabsRef = useRef(null);
  useEffect(() => { setTab("overview"); }, [internetInfo?.results?.acc_id, internetInfo?.results?.username_org]);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onPointerDown = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setActionsOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActionsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [actionsOpen]);

  useEffect(() => {
    if (!customerExists) {
      setProfilePicURL(null);
      setIdProofURL(null);
      setAddressProofURL(null);
      return;
    }

    // Profile picture
    if (data.profile_pic?.includes("https://vrplay.in")) {
      setProfilePicURL(data.profile_pic);
    } else {
      setProfilePicURL(data.profile_pic_url || null);
    }

    // ID proof
    if (data.id_proof?.includes("https://vrplay.in")) {
      setIdProofURL(data.id_proof);
    } else {
      setIdProofURL(data.id_proof_url || null);
    }

    // Address proof
    if (data.address_proof?.includes("https://vrplay.in")) {
      setAddressProofURL(data.address_proof);
    } else {
      setAddressProofURL(data.address_proof_url || null);
    }
  }, [
    customerExists,
    data.profile_pic,
    data.profile_pic_url,
    data.id_proof,
    data.id_proof_url,
    data.address_proof,
    data.address_proof_url
  ]);

  const isActive = (expiry) => {
    const parsed = parseExpiry(expiry);
    if (!parsed) return false;
    return parsed >= new Date();
  };

  const formatDate = (date) => {
    const parsed = parseExpiry(date);

    if (!parsed) return "--";

    return parsed.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const active = data.status === 1 && isActive(data.expiration || data.expiry_date);

  const canRecharge = data.verified === "Verified" && (data.aadhar_verified === 1 || !aadhaarRequired);

  const tabsMode = typeof renderPanel === "function";
  const needsVerify = data.verified !== "Verified";
  const needsAadhaar = data.aadhar_verified === 0;
  const tabAvailable = (key) =>
    key === "overview" ||
    ["password", "removemac", "onu", "authlogs", "sessions", "online"].includes(key) ||
    (key === "verify" && needsVerify) ||
    (key === "aadhaar" && needsAadhaar);
  const currentTab = tabsMode && tabAvailable(tab) ? tab : "overview";

  // Opens a tab (or the old pop-up when no renderPanel is given). `load` fetches the tab's data.
  const openTab = (key, load) => {
    if (!tabsMode) {
      load && load();
      return;
    }
    setTab(key);
    if (load && key !== "overview") load();
    // Mobile: keep the selected tab visible in the horizontal strip.
    window.requestAnimationFrame(() => {
      const el = tabsRef.current && tabsRef.current.querySelector(`[data-tab="${key}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    });
  };
  const tabBtn = (key, Icon, label, load, extraClass = "") => (
    <button
      type="button"
      role="tab"
      data-tab={key}
      aria-selected={currentTab === key}
      className={`internet-action ${extraClass} ${tabsMode && currentTab === key ? "is-selected" : ""}`}
      onClick={() => openTab(key, load)}
    >
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </button>
  );

  const comboNote = comboLock ? (
    <div className="internet-combo-lock" role="note">
      <strong>On combo{comboLock.name ? ` “${comboLock.name}”` : ""}</strong>
      <span>
        {comboLock.till ? `Till ${comboLock.till}. ` : ""}Renew it from Combo Recharge.
      </span>
    </div>
  ) : null;

  const pickLink = Array.isArray(linkTypes) && linkTypes.length > 1 && typeof onLinkType === "function";
  const [linkMenu, setLinkMenu] = useState(false);
  const linkRef = useRef(null);
  useEffect(() => {
    if (!linkMenu) return undefined;
    const close = (e) => {
      if (linkRef.current && !linkRef.current.contains(e.target)) setLinkMenu(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
    };
  }, [linkMenu]);

  return (
    <div className="internet-plan-card">
      {/* Still checking whether an account exists at all — distinct from
          "confirmed no account", which is the block below. Without this
          split, the empty state below would flash on screen for every
          customer, including ones who definitely have an account linked,
          for the moment before internetInfo actually arrives. */}
      {!customerExists && accountLoading && (
        <div className="internet-empty-state">
          <span className="card-loading-spinner"></span>
          <p className="internet-empty-title" style={{ marginTop: 12 }}>
            Loading {providerLabel} account details…
          </p>
        </div>
      )}

      {!customerExists && !accountLoading && accountLoadFailed && (
        <div className="internet-empty-state">
          <div className="internet-empty-icon">⚠️</div>
          <p className="internet-empty-title">Couldn't load {providerLabel} account details</p>
          <p className="internet-empty-subtitle">The {providerLabel} server didn't respond. Please try again.</p>
          <button className="internet-btn-primary" onClick={onRetry}>↻ Try again</button>
        </div>
      )}

      {!customerExists && !accountLoading && !accountLoadFailed && (
        <div className="internet-empty-state">
          <div className="internet-empty-icon">🌐</div>
          <p className="internet-empty-title">
            {pickLink ? "No Internet / Broadband connection linked" : `No ${providerLabel} / Broadband connection linked`}
          </p>
          <p className="internet-empty-subtitle">
            {pickLink
              ? `Link this customer to an ${linkTypes.map((t) => LINK_TYPE_LABEL[t] || t).join(", ").replace(/, ([^,]*)$/, " or $1")} account to manage broadband services.`
              : `Link this customer to ${/^[aeiou]/i.test(providerLabel) ? "an" : "a"} ${providerLabel} account to manage broadband services.`}
          </p>
          {pickLink ? (
            <div className="internet-link-pick" ref={linkRef}>
              <button
                type="button"
                className="internet-btn-primary"
                onClick={() => setLinkMenu((m) => !m)}
                disabled={loading}
                aria-haspopup="menu"
                aria-expanded={linkMenu}
              >
                {loading ? (
                  <>
                    <span className="internet-spinner-small"></span>
                    Checking...
                  </>
                ) : (
                  <>Link account ▾</>
                )}
              </button>
              {linkMenu && (
                <div className="internet-link-pick__menu" role="menu">
                  {linkTypes.map((t) => (
                    <button key={t} type="button" role="menuitem" onClick={() => { setLinkMenu(false); onLinkType(t); }}>
                      {LINK_TYPE_LABEL[t] || t} account
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              className="internet-btn-primary"
              onClick={onLinkAccount}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="internet-spinner-small"></span>
                  Checking...
                </>
              ) : (
                `Link ${providerLabel} Account`
              )}
            </button>
          )}
        </div>
      )}

      {customerExists && (
        <>
          <div className="internet-layout" ref={actionsRef}>
            {/* ACTIONS SIDEBAR — same actions and conditions as the old
                horizontal toolbar, grouped and stacked vertically. On
                smaller screens this same panel becomes the dropdown of
                the "⋮" button below (see InternetPlanCard.css). Any
                action tapped inside it closes the menu. */}
            <aside
              id={`internet-actions-${providerLabel}`}
              ref={tabsRef}
              className={`internet-actions ${tabsMode ? "internet-actions--tabs" : ""} ${actionsOpen ? "is-open" : ""}`}
              role={tabsMode ? "tablist" : undefined}
              aria-label={`${providerLabel} actions`}
              onClick={(e) => {
                if (e.target.closest("button")) setActionsOpen(false);
              }}
            >
              {tabsMode && (
                <div className="internet-actions__group internet-actions__group--overview">
                  {tabBtn("overview", FiInfo, "Overview", null)}
                </div>
              )}
              <div className="internet-actions__group internet-actions__group--billing">
                <p className="internet-actions__title">Billing</p>
                {canRecharge && (
                  comboLock ? comboNote : !active ? (
                    <button
                      type="button"
                      className="internet-action internet-action--primary"
                      disabled={loading}
                      onClick={() => onRecharge(false)}
                    >
                      <FiCreditCard aria-hidden="true" />
                      <span>Recharge</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="internet-action internet-action--primary"
                      disabled={loading}
                      onClick={() => onRecharge(true)}
                    >
                      <FiRefreshCw aria-hidden="true" />
                      <span>Advance Renewal</span>
                    </button>
                  ))}
                {needsAadhaar && tabBtn("aadhaar", FiShield, "Verify Aadhaar", tabsMode ? null : onVerifyAadhaar, "internet-action--attention")}
                {needsVerify && tabBtn("verify", FiUserCheck, "Verify Customer", tabsMode ? null : onChangeVerification, "internet-action--attention")}
              </div>

              <div className="internet-actions__group">
                <p className="internet-actions__title">Account</p>
                {tabBtn("password", FiKey, "Password", tabsMode ? null : onPassword)}
                {tabBtn("removemac", FiXCircle, "Remove MAC", tabsMode ? null : onRemoveMac)}
              </div>

              <div className="internet-actions__group">
                <p className="internet-actions__title">Diagnostics</p>
                {tabBtn("onu", FiCpu, "ONU Details", onOnuDetails)}
                {tabBtn("authlogs", FiLock, "Authentication Logs", onAuthLogs)}
                {tabsMode && tabBtn("online", FiActivity, "Online Session", null)}
                {tabBtn("sessions", FiClock, "Session History", tabsMode ? null : onSessionHistory)}
              </div>

              {user.role === "superadmin" && (
                <div className="internet-actions__group">
                  <p className="internet-actions__title">Documents</p>
                  <label className="internet-action internet-action--select">
                    <FiFileText aria-hidden="true" />
                    <select
                      className="internet-select"
                      defaultValue=""
                      aria-label="Open a customer document"
                      onChange={(e) => {
                        if (!e.target.value) return;
                        window.open(e.target.value, "_blank");
                        e.target.value = "";
                      }}
                    >
                      <option value="">Open document…</option>
                      {data.id_proof && (
                        <option value={`${idProofURL}`}>ID Proof</option>
                      )}
                      {data.address_proof && (
                        <option value={`${addressProofURL}`}>Address Proof</option>
                      )}
                    </select>
                  </label>
                </div>
              )}
            </aside>

            <div className="internet-layout__main">
              {/* Compact bar shown only when the sidebar is collapsed:
                  the main renewal action stays one tap away, everything
                  else lives behind the three-dots menu. */}
              <div className="internet-mobilebar">
                {canRecharge && comboLock ? (
                  comboNote
                ) : canRecharge ? (
                  <button
                    type="button"
                    className="internet-action internet-action--primary internet-mobilebar__primary"
                    disabled={loading}
                    onClick={() => onRecharge(active)}
                  >
                    {active ? <FiRefreshCw aria-hidden="true" /> : <FiCreditCard aria-hidden="true" />}
                    <span>{active ? "Advance Renewal" : "Recharge"}</span>
                  </button>
                ) : (
                  <span className="internet-mobilebar__note">Verification pending — see the tabs below</span>
                )}
                {!tabsMode && <button
                  type="button"
                  className={`internet-more-btn ${actionsOpen ? "is-open" : ""}`}
                  aria-label={`${providerLabel} actions`}
                  aria-haspopup="true"
                  aria-expanded={actionsOpen}
                  aria-controls={`internet-actions-${providerLabel}`}
                  onClick={() => setActionsOpen((o) => !o)}
                >
                  <FiMoreVertical aria-hidden="true" />
                </button>}
              </div>

              {/* HEADER */}
              <div className="internet-dashboard-header">
                <div className="internet-dashboard-left">
                  <div className="internet-big-icon">

                    <img
                      src={profilePicURL || defaultProfilePic}
                      alt="Customer"
                      className="internet-profile-pic"
                      onError={(e) => {
                        e.currentTarget.src = defaultProfilePic;
                      }}
                    />

                  </div>
                  <div>
                    <div className="internet-header-subtitle">
                      <span className="internet-copy">

                        <strong>Username:</strong> {data.username_org}
                        &nbsp;&nbsp;
                        <ContentCopyIcon
                          className="copy-icon"
                          fontSize="small"
                          onClick={() => {

                            navigator.clipboard
                              .writeText(data.username_org)
                              .then(() => {

                                toast.success(
                                  "Username copied!"
                                );

                              })
                              .catch(() => {

                                toast.error(
                                  "Unable to copy."
                                );

                              });

                          }}
                        />

                      </span>
                      <span>
                        <strong>Account ID:</strong> {data.acc_id}
                      </span>
                    </div>
                    <div className="internet-header-subtitle">
                      <span>
                        <strong>Mobile:</strong> {data.mobile}
                      </span>
                    </div>
                    <div className="internet-header-subtitle">
                      <span>
                        <strong>Expiry Date:</strong> {data.expiry_date ? formatDate(data.expiry_date) : "--"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="internet-dashboard-right">
                  <span
                    className={`internet-status-badge ${data.online === "ONLINE"
                      ? "online"
                      : "offline"
                      }`}
                  >

                    <span className="status-dot"></span>

                    {data.online}

                  </span>
                  <span
                    className={`internet-status-badge ${data.status === 1 ? "active" : "warning"
                      }`}
                  >
                    {data.status_text}
                  </span>
                </div>
              </div>

              {currentTab !== "overview" ? (
                <div className="internet-tabpanel" role="tabpanel" key={currentTab}>
                  {renderPanel(currentTab, () => setTab("overview"))}
                </div>
              ) : (
              <>
              {/* DASHBOARD GRID */}
              <div className="internet-dashboard-grid">
                {/* Account Details */}
                <div className="internet-dashboard-card">
                  <h3>Account Details</h3>
                  <div className="internet-detail-row">
                    <span>Username</span>
                    <strong>{data.username_org}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Customer</span>
                    <strong>
                      {[data.firstname, data.lastname].filter(Boolean).join(" ")}
                    </strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Email</span>
                    <strong>{data.email || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Mobile</span>
                    <strong
                      style={{
                        color: data.mobile_verify === "Verified" ? "#15803d" : "#b42318",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      {data.mobile}
                      {data.mobile_verify === "Verified" ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      )}
                    </strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Customer Verification</span>
                    <strong
                      style={{
                        color: data.verified === "Verified" ? "#15803d" : "#b42318",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      {data.verified}
                      {data.verified === "Verified" ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      )}
                    </strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Aadhar Verification</span>
                    <strong
                      style={{
                        color: data.aadhar_verified === 1 ? "#15803d" : "#b42318",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      {data.aadhar_verified === 1 ? "Verified" : "UnVerified"}
                      {data.aadhar_verified === 1 ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      )}
                    </strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Aadhar Verification Date</span>
                    <strong>{data.aadhar_verified_time === "" ? <p>-- &nbsp;&nbsp;</p> : data.aadhar_verified_time}</strong>
                  </div>
                </div>

                {/* Package Details */}
                <div className="internet-dashboard-card">
                  <h3>Package Details</h3>
                  <div className="internet-detail-row">
                    <span>Package</span>
                    <strong>{data.package}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Sub Plan</span>
                    <strong>{data.sub_plan}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Expiry</span>
                    <strong>{formatDate(data.expiry_date)}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Status</span>
                    <strong>{data.status_text || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Last Renewal</span>
                    <strong>{data.renewal_date ? formatDate(data.renewal_date) : "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Registered On</span>
                    <strong>{data.registered_date ? formatDate(data.registered_date) : "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Partner</span>
                    <strong>{(data.partner || "").trim() || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Branch</span>
                    <strong>{data.branch || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Static IP</span>
                    <strong>
                      {Number(data.has_static_ip) === 1 && Array.isArray(data.static_ip_list) && data.static_ip_list.length
                        ? data.static_ip_list.map((ip) => (typeof ip === "string" ? ip : ip?.ip || ip?.ip_address || "")).filter(Boolean).join(", ")
                        : "No"}
                    </strong>
                  </div>
                </div>

                {/* Connection Details */}
                <div className="internet-dashboard-card">
                  <h3>Connection Details</h3>
                  <div className="internet-detail-row">
                    <span>Online Status</span>
                    <strong
                      style={{
                        color: data.online === "ONLINE" ? "#15803d" : "#b42318"
                      }}
                    >
                      {data.online || "--"}
                    </strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Activation Date</span>
                    <strong>{data.activation_date || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Last Logout</span>
                    <strong>{data.last_log_off || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>State</span>
                    <strong>{data.state || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Customer Type</span>
                    <strong>{data.customer_type || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Simultaneous Use</span>
                    <strong>{data.simultanious_use || "--"}</strong>
                  </div>
                  <div className="internet-detail-row">
                    <span>Subscription</span>
                    <strong>
                      {data.subscription_type === 0 ? "Prepaid" : "Postpaid"}
                    </strong>
                  </div>
                </div>
              </div>

              {/* ADDRESS INFORMATION */}
              <div className="internet-dashboard-card internet-address-card">
                <h3>Address Information</h3>
                <div className="internet-address-grid">
                  <div>
                    <h4>Billing Address</h4>
                    <p>{data.billing_address || "--"}</p>
                  </div>
                  <div>
                    <h4>Installation Address</h4>
                    <p>{data.installation_address || "--"}</p>
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default InternetPlanCard;
